import { STUDIO_BOT } from '@/constants/blockchain'
import { get, post, processBalance, ProcessedBalance } from '@/utils/utils'

// Cast Votes
export interface CastVotesPayload {
  votes: {
    candidate: string
    amount: string
  }[]
  signature: string
}

export interface CastVotesResponse {
  valid: boolean
  error?: string
}

// Get total votes of a candidate
interface GetVotesForCandidatePayload {
  address: string
}
interface GetVotesForCandidateResponse {
  // with 18 decimals
  votes: string
  error?: string
}

type StatsIntervalOpts = '30m' | '1h' | '2h' | '4h' | '6h' | '8h' | '12h' | '24h'

// Get candidate stats
interface GetElectionStatsPayload {
  electionIdx: number
  interval: StatsIntervalOpts
}

interface GetElectionStatsResponse {
  stats: {
    candidates: string[]
    data: {
      timestamp: number
      amounts: string[]
    }[]
  }
  error?: string
}

// Get VOTE tokens used for voting
interface GetUsedVotesBalanceResponse {
  // with 18 decimals
  balance: string
  error?: string
}

// Get user's votes
interface GetUserVotesPayload {
  electionIdx: number
}

export type UserVote = {
  candidate: string
  amount: string // with 18 decimals
  proof: string[] // hex strings
  leafIndex: number
}

export type UserVotes = {
  timestamp: string
  signature: string
  votes: UserVote[]
}

export interface GetUserVotesResponse {
  votes: UserVotes[]
  valid: boolean
  error?: string
}

// Server Requests

export async function fetchAdmins(): Promise<string[]> {
  const response = await get(`${STUDIO_BOT}/admins`)
  return response as string[]
}

export async function fetchBalance(token: string): Promise<ProcessedBalance> {
  const response = (await get(
    `${STUDIO_BOT}/elections/balance`,
    token
  )) as GetUsedVotesBalanceResponse
  if (!response.error) {
    return processBalance(BigInt(response.balance))
  }
  throw new Error(response.error)
}

export async function fetchGetVotes(token: string, electionIdx: number): Promise<UserVotes[]> {
  const payload: GetUserVotesPayload = { electionIdx }
  const response = (await post(
    `${STUDIO_BOT}/elections/getVotes`,
    JSON.stringify(payload),
    token
  )) as GetUserVotesResponse
  if (!response.error) {
    return response.votes
  }
  throw new Error(response.error)
}

export type UnsplitVotes = {
  timestamp: string
  candidate: string
  amount: ProcessedBalance
}

export function unsplitVotes(userVotes: UserVotes[]): UnsplitVotes[] {
  const finalVotes: UnsplitVotes[] = []
  userVotes.forEach(vote => {
    vote.votes.forEach(ballot => {
      const existingBallot = finalVotes.find(
        v => v.candidate === ballot.candidate && v.timestamp === vote.timestamp
      )
      if (existingBallot) {
        existingBallot.amount = processBalance(existingBallot.amount.value + BigInt(ballot.amount))
      } else {
        finalVotes.push({
          candidate: ballot.candidate,
          amount: processBalance(BigInt(ballot.amount)),
          timestamp: vote.timestamp,
        })
      }
    })
  })
  return finalVotes
}
export function processVoteTotals(userVotes: UserVotes[]): Record<string, ProcessedBalance> {
  return userVotes
    .flatMap(vt => vt.votes)
    .reduce(
      (acc, curr) => {
        if (acc[curr.candidate]) {
          acc[curr.candidate] = processBalance(acc[curr.candidate].value + BigInt(curr.amount))
        } else {
          acc[curr.candidate] = processBalance(BigInt(curr.amount))
        }
        return acc
      },
      {} as Record<string, ProcessedBalance>
    )
}

export async function fetchCandidate(address: string): Promise<ProcessedBalance> {
  const payload: GetVotesForCandidatePayload = { address }
  const response = (await post(
    `${STUDIO_BOT}/elections/candidate`,
    JSON.stringify(payload)
  )) as GetVotesForCandidateResponse
  if (!response.error) {
    return processBalance(BigInt(response.votes))
  }
  throw new Error(response.error)
}

export type ElectionStats = Record<string, string | number>[]

export async function fetchElectionStats(
  electionIdx: number,
  interval: StatsIntervalOpts = '24h'
): Promise<ElectionStats> {
  const payload: GetElectionStatsPayload = { electionIdx, interval }
  const response = (await post(
    `${STUDIO_BOT}/elections/stats`,
    JSON.stringify(payload)
  )) as GetElectionStatsResponse
  if (!response.error) {
    // Process stats
    const { stats } = response
    const processedStats: ElectionStats = []
    stats.data.forEach(timePoint => {
      processedStats.push({
        date: new Date(timePoint.timestamp * 1000).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        ...timePoint.amounts.reduce(
          (acc, amount, index) => {
            acc[stats.candidates[index]] = amount
            return acc
          },
          {} as Record<string, string | number>
        ),
      })
    })
    return processedStats
  }
  throw new Error(response.error)
}

export type CurrentVote = {
  user: string
  candidate: string
  amount: string
  index: number
}

interface GetAdminVotesResponse {
  votes: CurrentVote[]
  valid: boolean
  error?: string
}

export async function fetchAdminVotes(token: string): Promise<CurrentVote[]> {
  const response = (await get(
    `${STUDIO_BOT}/elections/admin/votes`,
    token
  )) as GetAdminVotesResponse
  if (!response.error) {
    return response.votes
  }
  throw new Error(response.error)
}
